import { BUTTER, MINT, PAPER, lerpColor } from '../../color';
import type { WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const CRATERS = [-0.3, -0.25, 0.16, 0.28, 0.05, 0.12, -0.05, 0.4, 0.1, 0.36, -0.42, 0.07];
const RUNES = 6;
const DASHES = 28;

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    k.bands(body, lerpColor(color, PAPER, 0.35), 0.45 * alpha);
    k.terminator(body, lerpColor(color, k.pal.ink, 0.55), 0.55 * alpha);
    k.rimLight(body, 0.6 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    for (let i = 0; i < CRATERS.length; i += 3) {
      const s = CRATERS[i + 2]! * r;
      g.ellipse(body.x + CRATERS[i]! * r * body.scaleX, body.y + CRATERS[i + 1]! * r * body.scaleY, s * body.scaleX, s * body.scaleY);
    }
    g.fill({ color: lerpColor(fill, pal.heldShade, 0.6), alpha: 0.55 * alpha });
    k.terminator(body, lerpColor(fill, pal.ink, 0.5), 0.45 * alpha);
    k.rimLight(body, 0.7 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.4), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    for (let i = 0; i < DASHES; i++) {
      const a0 = (i / DASHES) * TAU - spin * 0.5;
      const a1 = a0 + (TAU / DASHES) * 0.5;
      g.moveTo(body.x + Math.cos(a0) * reach, body.y + Math.sin(a0) * reach).arc(body.x, body.y, reach, a0, a1);
    }
    g.stroke({ width: 1.2, color: pal.spot, alpha: 0.5 * alpha });
    k.ringDots(body, reach, RUNES, spin, (x, y, _i, a) => {
      const s = Math.max(3, k.unit * 0.022);
      g.star(x, y, 4, s, s * 0.35, a);
    });
    g.fill({ color: PAPER, alpha: 0.75 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    g.star(x, y, 4, radius * 1.55, radius * 0.5, 0);
    if (on) g.fill({ color: last ? MINT : lerpColor(PAPER, BUTTER, 0.4), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(6, k.unit * 0.035);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const len = Math.hypot(points[i]! - x0, points[i + 1]! - y0);
      const n = Math.min(80, Math.floor(len / step));
      for (let s = 1; s < n; s++) {
        const t = s / n;
        g.circle(x0 + (points[i]! - x0) * t, y0 + (points[i + 1]! - y0) * t, 1.3);
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, _i, color, alpha, rot) {
    g.star(x, y, 4, size * 1.3, size * 0.35, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m) {
    g.circle(m.x, m.y, m.r).stroke({ width: m.width, color: BUTTER, alpha: m.fade * 0.55 });
    g.circle(m.x, m.y, m.r * 1.18).stroke({ width: m.width * 0.6, color: MINT, alpha: m.fade * 0.45 });
  },

  trail(g, x, y, size, angle) {
    g.star(x, y, 4, size * 1.2, size * 0.3, angle);
  },
};
